import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Check, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { PRODUCTS, CATALOGUE_URL, CATALOGUE_SPECS, PLANT_IMAGES, DOLOMITE_PLANT_IMAGES } from "../data/siteData";

const Products = () => {
  const [params, setParams] = useSearchParams();
  const active = PRODUCTS.some((p) => p.key === params.get("stone")) ? params.get("stone") : "granite";

  return (
    <main data-testid="products-page">
      <section className="bg-[#F8F9FA] py-20 md:py-28 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Our Products</Eyebrow>
            <h1 className="mt-4 max-w-3xl text-5xl md:text-6xl font-bold tracking-tighter" data-testid="products-heading">
              Granite, Marble, Quartzite, Tomb Stone and Dolomite — Under One Roof
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-gray-600" data-testid="products-intro">
              One supplier for the full range: exotic and superior granite, premium marble and quartzite for
              construction and interiors, and industrial dolomite for manufacturing. Specify your form and finish —
              we cut and pack to order.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Tabs value={active} onValueChange={(v) => setParams({ stone: v })} data-testid="products-tabs">
            <TabsList className="grid w-full max-w-3xl grid-cols-2 sm:grid-cols-5 rounded-none border border-gray-200 bg-[#F8F9FA] p-1" data-testid="products-tabs-list">
              {PRODUCTS.map((p) => (
                <TabsTrigger
                  key={p.key}
                  value={p.key}
                  data-testid={`tab-trigger-${p.key}`}
                  className="rounded-none py-3 text-sm font-bold uppercase tracking-wider data-[state=active]:bg-[#D40000] data-[state=active]:text-white"
                >
                  {p.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {PRODUCTS.map((p) => (
              <TabsContent key={p.key} value={p.key} className="mt-12" data-testid={`tab-content-${p.key}`}>
                <div className="grid gap-12 lg:grid-cols-12 items-start">
                  <div className="lg:col-span-12">
                    <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid={`product-name-${p.key}`}>
                      {p.name}
                    </h2>
                    <p className="mt-3 text-lg font-medium text-[#D40000]">{p.tagline}</p>
                    <p className="mt-5 text-base leading-relaxed text-gray-600" data-testid={`product-desc-${p.key}`}>
                      {p.description}
                    </p>
                    <div className="mt-8 grid gap-8 sm:grid-cols-2">
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Available Forms</h3>
                        <ul className="mt-4 space-y-2.5">
                          {p.forms.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                              <Check size={15} className="mt-0.5 shrink-0 text-[#D40000]" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Typical Uses</h3>
                        <ul className="mt-4 space-y-2.5">
                          {p.uses.map((u) => (
                            <li key={u} className="flex items-start gap-2 text-sm text-gray-700">
                              <Check size={15} className="mt-0.5 shrink-0 text-[#D40000]" /> {u}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                {p.series && p.series.length > 0 && (
                  <div className="mt-14 space-y-10" data-testid={`product-series-${p.key}`}>
                    {p.series.map((s) => (
                      <div key={s.name}>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#D40000]">{s.name}</h3>
                        <div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                          {s.items.map((item) => (
                            <Link
                              key={item.name}
                              to={`/contact?product=${encodeURIComponent(`${item.name} ${p.name}`)}`}
                              data-testid={`variety-${p.key}-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className="group border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-black"
                            >
                              <div className="h-48 md:h-56 overflow-hidden">
                                <img
                                  src={item.img}
                                  alt={`${item.name} ${p.name}`}
                                  loading="lazy"
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                              </div>
                              <p className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-800 group-hover:text-[#D40000]">
                                {item.name}
                              </p>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-14" data-testid={`plant-gallery-${p.key}`}>
                  <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#D40000]">From Our Processing Plant</h3>
                  <div className="mt-5 grid gap-5 sm:grid-cols-3">
                    {(p.key === "dolomite" ? DOLOMITE_PLANT_IMAGES : PLANT_IMAGES).map((pl) => (
                      <figure key={pl.name} className="group border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-black">
                        <div className="h-44 md:h-52 overflow-hidden">
                          <img
                            src={pl.img}
                            alt={pl.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            data-testid={`plant-img-${pl.name.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        </div>
                        <figcaption className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-800">{pl.name}</figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
                <div className="mt-12">
                  <div className="lg:col-span-6">
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                      <Link
                        to={`/contact?product=${p.name}`}
                        data-testid={`product-enquire-${p.key}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#D40000] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900"
                      >
                        Enquire about {p.name} <ArrowRight size={16} />
                      </Link>
                      <a
                        href={p.catalogue || CATALOGUE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-catalogue-${p.key}`}
                        className="inline-flex items-center justify-center gap-2 border border-black px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-1 hover:bg-black hover:text-white"
                      >
                        <Download size={16} /> Download Catalogue
                      </a>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Catalogue specs strip */}
          <Reveal className="mt-16">
            <div className="border border-gray-200 bg-[#F8F9FA] p-8" data-testid="catalogue-specs-strip">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-medium">Standard Specifications</h3>
                  <p className="mt-1 text-sm text-gray-600">As per our natural stone catalogue — customisation available on request.</p>
                </div>
                <a
                  href={CATALOGUE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="specs-catalogue-button"
                  className="inline-flex shrink-0 items-center gap-2 bg-black px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-[#D40000]"
                >
                  <Download size={16} /> Full Catalogue (PDF)
                </a>
              </div>
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ["Thickness", CATALOGUE_SPECS.thickness],
                  ["Type", CATALOGUE_SPECS.types],
                  ["Finishes", CATALOGUE_SPECS.finishes],
                  ["Sizes", CATALOGUE_SPECS.sizes],
                  ["Dispatch", CATALOGUE_SPECS.dispatch],
                  ["Minimum Order", CATALOGUE_SPECS.moq],
                ].map(([k, v]) => (
                  <div key={k} className="border-l-2 border-[#D40000] pl-4" data-testid={`spec-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">{k}</p>
                    <p className="mt-1 text-sm font-semibold text-black">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Products;
